import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';


// ─── Types ──────────────────────────────────────────────────────────
export interface HeatmapDatum {
  dayOfWeek: number; // 0=月 … 6=日
  hour: number;      // 0–23
  count: number;
}

export interface HeatmapChartProps {
  data: HeatmapDatum[];
}

// ─── Constants ──────────────────────────────────────────────────────
const DAY_LABELS = ['月', '火', '水', '木', '金', '土', '日'] as const;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// ─── Helpers ────────────────────────────────────────────────────────
function getCellColor(count: number, max: number): string {
  if (max === 0 || count === 0) return 'rgba(0,243,255,0.04)';

  const ratio = count / max;

  // Stepped opacity for cleaner visual
  if (ratio < 0.15) return 'rgba(0,243,255,0.08)';
  if (ratio < 0.3) return 'rgba(0,243,255,0.18)';
  if (ratio < 0.5) return 'rgba(0,243,255,0.32)';
  if (ratio < 0.7) return 'rgba(0,243,255,0.50)';
  if (ratio < 0.85) return 'rgba(0,243,255,0.70)';
  return 'rgba(0,243,255,0.90)';
}

// ─── Component ──────────────────────────────────────────────────────
export const HeatmapChart = ({ data }: HeatmapChartProps) => {
  const [hoveredCell, setHoveredCell] = useState<{
    day: number;
    hour: number;
    count: number;
    x: number;
    y: number;
  } | null>(null);

  // Build lookup map and compute max
  const { lookup, maxCount } = useMemo(() => {
    const map = new Map<string, number>();
    let max = 0;
    for (const d of data) {
      const key = `${d.dayOfWeek}-${d.hour}`;
      map.set(key, d.count);
      if (d.count > max) max = d.count;
    }
    return { lookup: map, maxCount: max };
  }, [data]);

  const getCount = (day: number, hour: number) =>
    lookup.get(`${day}-${hour}`) ?? 0;



  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="w-full"
    >
      <div className="rounded-xl border border-white/10 bg-black/30 p-5">
        <h3 className="mb-4 text-sm font-bold tracking-widest text-gray-400">
          アクティビティ・ヒートマップ
        </h3>

        <div className="relative overflow-x-auto">
          {/* Hour labels */}
          <div className="mb-1 flex pl-8">
            {HOURS.map((h) => (
              <div
                key={h}
                className="flex-1 text-center text-[9px] text-gray-500"
              >
                {h % 3 === 0 ? `${h}時` : ''}
              </div>
            ))}
          </div>

          {/* Grid rows */}
          <div className="flex flex-col gap-[2px]">
            {DAY_LABELS.map((dayLabel, dayIdx) => (
              <div key={dayIdx} className="flex items-center gap-[2px]">
                {/* Day label */}
                <div className="w-7 flex-shrink-0 text-right text-[11px] font-medium text-gray-500 pr-1">
                  {dayLabel}
                </div>

                {/* Hour cells */}
                {HOURS.map((hour, hourIdx) => {
                  const count = getCount(dayIdx, hour);
                  const cellIndex = dayIdx * 24 + hourIdx;

                  return (
                    <motion.div
                      key={hour}
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.2,
                        delay: cellIndex * 0.003,
                        ease: 'easeOut',
                      }}
                      className="flex-1 aspect-square min-w-[10px] rounded-[2px] cursor-pointer transition-transform hover:scale-125 hover:z-10"
                      style={{
                        backgroundColor: getCellColor(count, maxCount),
                      }}
                      onMouseEnter={(e) => {
                        const rect = (
                          e.target as HTMLElement
                        ).getBoundingClientRect();
                        setHoveredCell({
                          day: dayIdx,
                          hour,
                          count,
                          x: rect.left + rect.width / 2,
                          y: rect.top,
                        });
                      }}
                      onMouseLeave={() => setHoveredCell(null)}
                    />
                  );
                })}
              </div>
            ))}
          </div>

          {/* Tooltip */}
          {hoveredCell && (
            <div
              className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded-lg border border-white/10 bg-black/90 px-3 py-2 shadow-lg backdrop-blur-sm"
              style={{
                left: hoveredCell.x,
                top: hoveredCell.y - 8,
              }}
            >
              <p className="text-[11px] text-gray-400">
                {DAY_LABELS[hoveredCell.day]}曜日 {hoveredCell.hour}:00
              </p>
              <p className="text-sm font-bold text-neon-blue">
                {hoveredCell.count}件
              </p>
            </div>
          )}

          {/* Legend */}
          <div className="mt-3 flex items-center justify-end gap-1.5">
            <span className="text-[10px] text-gray-500">少</span>
            {[0.04, 0.15, 0.32, 0.55, 0.8].map((opacity, i) => (
              <div
                key={i}
                className="h-2.5 w-2.5 rounded-[2px]"
                style={{ backgroundColor: `rgba(0,243,255,${opacity})` }}
              />
            ))}
            <span className="text-[10px] text-gray-500">多</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
